import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {TranslateService} from '@ngx-translate/core';
import {BzDialogService} from '@bringoz/ui';
import {TermsHttpService} from '../../services/terms-http.service';
import {TermsStatus, TermsVersion} from '../../models/terms-version.model';
import {
  TERMS_LIST_PAGE_SIZE,
  TERMS_ROW_ACTIONS,
  TERMS_STATUS_TONE,
  TERMS_TABLE_COLUMN_WIDTHS,
  TERMS_TABLE_ROW_HEIGHT,
  TERMS_TABLE_UNIQUE_ID
} from '../../constants/terms-table.constant';
import {
  TermsEditorDialogComponent,
  TermsEditorDialogData
} from '../terms-editor-dialog/terms-editor-dialog.component';

require('./terms-view.scss');
@Component({
  standalone: false,
  selector: 'app-terms-view',
  templateUrl: './terms-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TermsViewComponent implements OnInit {

  public readonly tableUniqueId = TERMS_TABLE_UNIQUE_ID;
  public readonly defaultWidths = TERMS_TABLE_COLUMN_WIDTHS;
  public readonly rowHeight = TERMS_TABLE_ROW_HEIGHT;
  public readonly statusTone = TERMS_STATUS_TONE;
  public readonly rowActions = TERMS_ROW_ACTIONS;
  public readonly statuses = TermsStatus;

  public versions: TermsVersion[] = [];
  public isLoading = false;
  public isApprovalsOpen = false;

  private nextCursor = '';

  constructor(@Inject(TermsHttpService) private termsHttpService: TermsHttpService,
              @Inject(BzDialogService) private dialogs: BzDialogService,
              @Inject(TranslateService) private translate: TranslateService,
              @Inject('bringozToastService') private bringozToastService: any,
              @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef) {
  }

  ngOnInit() {
    this.loadVersions();
  }

  onFetchNext() {
    if (this.nextCursor && !this.isLoading) {
      this.loadVersions(this.nextCursor);
    }
  }

  onCreate() {
    this.openEditor({});
  }

  // Only Drafts are editable - any other status opens the same dialog read-only.
  onOpen(version: TermsVersion) {
    this.openEditor({version, readOnly: version.status !== TermsStatus.Draft});
  }

  // There is no duplicate endpoint: a duplicate is a new Draft with the source's title and content,
  // whatever the source's status.
  onDuplicate(version: TermsVersion) {
    this.termsHttpService.create({title: version.title, content: version.content, status: TermsStatus.Draft}).subscribe({
      next: () => this.onChanged('TERMS_DUPLICATE_SUCCESS'),
      error: (err: HttpErrorResponse) => this.showError('TERMS_DUPLICATE_ERROR', err)
    });
  }

  onPublish(version: TermsVersion) {
    this.confirmStatusChange(version, TermsStatus.Published, {
      title: 'TERMS_PUBLISH_CONFIRM_TITLE',
      message: 'TERMS_PUBLISH_CONFIRM_TEXT',
      confirmLabel: 'Publish',
      successKey: 'TERMS_PUBLISH_SUCCESS',
      errorKey: 'TERMS_PUBLISH_ERROR',
      destructive: false
    });
  }

  onArchive(version: TermsVersion) {
    this.confirmStatusChange(version, TermsStatus.Archived, {
      title: 'TERMS_ARCHIVE_CONFIRM_TITLE',
      message: 'TERMS_ARCHIVE_CONFIRM_TEXT',
      confirmLabel: 'Archive',
      successKey: 'TERMS_ARCHIVE_SUCCESS',
      errorKey: 'TERMS_ARCHIVE_ERROR',
      destructive: true
    });
  }

  onOpenApprovals() {
    this.isApprovalsOpen = true;
  }

  onCloseApprovals() {
    this.isApprovalsOpen = false;
  }

  private openEditor(data: TermsEditorDialogData) {
    this.dialogs.open<TermsEditorDialogComponent, TermsVersion, TermsEditorDialogData>(TermsEditorDialogComponent, {
      data,
      width: '720px'
    }).then((saved) => {
      if (saved) {
        this.onChanged(data.version ? 'TERMS_UPDATE_SUCCESS' : 'TERMS_CREATE_SUCCESS');
      }
    });
  }

  private confirmStatusChange(version: TermsVersion, status: TermsStatus,
                              texts: { title: string, message: string, confirmLabel: string, successKey: string, errorKey: string, destructive: boolean }) {
    this.dialogs.confirm({
      title: this.translate.instant(texts.title),
      message: this.translate.instant(texts.message, {title: version.title, version: version.versionNumber}),
      confirmLabel: this.translate.instant(texts.confirmLabel),
      cancelLabel: this.translate.instant('Cancel'),
      destructive: texts.destructive
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      this.termsHttpService.setStatus(version.id, status).subscribe({
        next: () => this.onChanged(texts.successKey),
        error: (err: HttpErrorResponse) => this.showError(texts.errorKey, err)
      });
    });
  }

  // Publishing also archives the previous Published version, so any change reloads the list
  // from the first page rather than patching a single row.
  private onChanged(successKey: string) {
    this.bringozToastService.success({body: successKey});
    this.loadVersions();
  }

  private loadVersions(cursor?: string) {
    this.isLoading = true;
    this.cdr.markForCheck();

    const params = cursor ? {cursor} : {limit: TERMS_LIST_PAGE_SIZE};
    this.termsHttpService.getList(params).subscribe({
      next: (res) => {
        this.versions = cursor ? [...this.versions, ...res.list] : res.list;
        this.nextCursor = res.cursor;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading = false;
        this.cdr.markForCheck();
        this.showError('TERMS_LOAD_ERROR', err);
      }
    });
  }

  // bringoz-toast translates `body` / `details` under the 'alerts.' namespace; the server's own
  // validation text (more_info) is shown as-is.
  private showError(bodyKey: string, err: HttpErrorResponse) {
    const moreInfo: string = err?.error?.more_info;
    this.bringozToastService.error({
      body: bodyKey,
      message: moreInfo,
      details: moreInfo ? undefined : 'SOMETHING_WENT_WRONG'
    });
  }

}
